import React, { useState, useEffect } from "react";
import { Avatar, Box, getColor, Icons, Tag, Tooltip, Typography } from "fds";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import classNames from "classnames";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import useMeetingStore from "../../redux/dispatcher/useMeetingStore";
import { MeetingDetailDto } from "../../types/meeting";
import { t } from "i18next";
import NameTag from "../NameTag";
interface MeetingAsideProps {
  asideId?: string;
  meetingName?: string;
  classes?: string;
}
const MeetingAside: React.FC<MeetingAsideProps> = ({
  asideId,
  meetingName,
  classes,
}) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const { meetingState, getMeetingDetail } = useMeetingStore();
  const [meetingStatus, changeMeetingStatus] = useState("finished");
  const [meetingDetail, changeMeetingDetail] = useState<
    MeetingDetailDto | undefined
  >();
  useEffect(() => {
    const [workgroupId, meetingId, occurrenceId] = asideId?.split("@@") ?? [];
    if (!workgroupId || !meetingId) return;
    getMeetingDetail({
      workgroupId: workgroupId ?? "",
      meetingId,
      occurrenceId,
    });
  }, [asideId]);
  useEffect(() => {
    changeMeetingDetail(meetingState.meetingDetail);
    if (meetingState.meetingDetail) {
      changeMeetingStatus(
        dayjs(meetingState.meetingDetail.meetingBasicInfo.endTime).valueOf() >
          dayjs().valueOf() &&
          dayjs(
            meetingState.meetingDetail.meetingBasicInfo.startTime
          ).valueOf() < dayjs().valueOf()
          ? "live"
          : dayjs(
              meetingState.meetingDetail.meetingBasicInfo.endTime
            ).valueOf() > dayjs().valueOf()
          ? "waiting"
          : "finished"
      );
    } else if (asideId) {
      changeMeetingStatus("DELETED");
    }
  }, [meetingState.meetingDetail]);
  return meetingDetail ? (
    <Box classes={classNames("meetingAside", classes)}>
      <Box classes="meetingAside-graybox">
        <Box classes="meetingAside-thumbnail">
          <img
            src={require("../../shared/images/pages/Meeting/detailThumbnail.png")}
            alt={"thumbnail"}
            height={"100%"}
          />
        </Box>
        <Tooltip
          message={meetingDetail.zoomMeetingDetailDTO.topic}
          classes="mg mt-10 "
        >
          <Typography type="subtitle2" ellipsis>
            {meetingDetail.zoomMeetingDetailDTO.topic}
          </Typography>
        </Tooltip>
      </Box>
      {meetingDetail.zoomMeetingDetailDTO.agenda && (
        <Box classes="mg mt-24" direction="column">
          <Typography type="subtitle2">{t("meeting.content")}</Typography>
          <Typography type="body2_rg" classes={"mg mt-8"} whiteSpace="normal">
            {meetingDetail.zoomMeetingDetailDTO.agenda}
          </Typography>
        </Box>
      )}
      <Box classes="mg mt-24" direction="column">
        <Typography type="subtitle2">{t("meeting.meetingId")}</Typography>
        <Typography type="body2_rg" classes={"mg mt-8"} whiteSpace="normal">
          {meetingDetail.zoomMeetingDetailDTO.id}
        </Typography>
      </Box>
      <Box classes="mg mt-24" direction="column">
        <Typography type="subtitle2">{t("meeting.status")}</Typography>
        <Typography type="body2_rg" classes={"mg mt-8"} whiteSpace="normal">
          {meetingStatus === "waiting"
            ? t("meeting.upcoming")
            : meetingStatus === "live"
            ? t("meeting.inProgress")
            : t("meeting.finish")}
        </Typography>
      </Box>
      <Box classes="mg mt-24" direction="column">
        <Typography type="subtitle2">{t("meeting.time")}</Typography>
        <Typography
          type="body2_rg"
          classes={"mg mt-8"}
          whiteSpace="break-spaces"
        >
          {`${utilsCommon.renderDateFormat(
            meetingDetail.zoomMeetingDetailDTO.start_time
          )} \n - ${utilsCommon.renderDateFormat(
            dayjs(meetingDetail.zoomMeetingDetailDTO.start_time)
              .add(meetingDetail.zoomMeetingDetailDTO.duration, "minute")
              .valueOf()
          )} (GMT${utilsCommon.renderDateFormat(
            meetingDetail.zoomMeetingDetailDTO.start_time,
            "Z"
          )})`}
        </Typography>
      </Box>
      <Box classes="mg mt-24" direction="column">
        <Typography type="subtitle2">{t("file.lastUpdate")}</Typography>
        <Typography type="body2_rg" classes={"mg mt-8"} whiteSpace="normal">
          {utilsCommon.renderDateFormat(
            meetingDetail.meetingBasicInfo.updateTime
          )}
        </Typography>
      </Box>
      <Box classes="mg mt-24" direction="column">
        <Typography type="subtitle2">{t("meeting.securtiy")}</Typography>
        <Box classes={"mg mt-8"} alignItems="center">
          <Typography type="body2_rg" whiteSpace="normal">
            {t("meeting.password")}
          </Typography>
          {meetingDetail.zoomMeetingDetailDTO.password ? (
            <Box>
              {" "}
              <Icons
                variant="line"
                label="check"
                stroke={getColor("COLOR_ICON_SUCCESS")}
                classes={"mg ml-8 mr-8"}
              />
              <Tooltip message={meetingDetail.zoomMeetingDetailDTO.password}>
                {Array(meetingDetail.zoomMeetingDetailDTO.password.length)
                  .fill("*")
                  .map((item) => item)}
              </Tooltip>
            </Box>
          ) : (
            <Typography
              type="body2_rg"
              classes="mg ml-8"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("common.noUse")}
            </Typography>
          )}
        </Box>

        <Box classes={"mg mt-8"} alignItems="center">
          <Typography type="body2_rg" whiteSpace="normal">
            {t("meeting.waitingRoom")}
          </Typography>
          {meetingDetail.zoomMeetingDetailDTO.settings.waiting_room ? (
            <Icons
              variant="line"
              label="check"
              stroke={getColor("COLOR_ICON_SUCCESS")}
              classes={"mg ml-8 mr-8"}
            />
          ) : (
            <Typography
              type="body2_rg"
              classes="mg ml-8"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("common.noUse")}
            </Typography>
          )}
        </Box>
      </Box>
      {meetingDetail.meetingBasicInfo.workgroupName && (
        <Box classes="mg mt-24" direction="column">
          <Typography type="subtitle2">{t("common.workgroupName")}</Typography>
          <Tooltip message={meetingDetail.meetingBasicInfo.workgroupName}>
            <Typography
              type="body2_rg"
              classes={"mg mt-8"}
              whiteSpace="normal"
              ellipsis
            >
              {meetingDetail.meetingBasicInfo.workgroupName}
            </Typography>
          </Tooltip>
        </Box>
      )}
      <Box classes="mg mt-24" direction="column">
        <Typography type="subtitle2">{t("meeting.participants")}</Typography>
        <Box classes="mg mt-8" direction="column">
          {meetingDetail?.participantList
            .slice()
            ?.sort((a, b) =>
              a.userId === meetingDetail.meetingBasicInfo.ownerId
                ? -1
                : b.userId === meetingDetail.meetingBasicInfo.ownerId
                ? 1
                : 0
            )
            ?.map((user) => (
              <NameTag
                user={user}
                classes="meetingAside-participant"
                withoutUserId={false}
              />
            ))}
        </Box>
      </Box>
    </Box>
  ) : meetingStatus === "DELETED" ? (
    <Box classes={classNames("meetingAside", classes)}>
      <Box classes="meetingAside-graybox">
        <Box classes="meetingAside-thumbnail">
          <img
            src={require("../../shared/images/pages/Meeting/DeletedMeeting.png")}
            alt={"thumbnail"}
            width={220}
            height={120}
          />
        </Box>
        <Typography
          type="body4_rg"
          ellipsis
          classes="mg mt-10"
          exactColor={getColor("COLOR_TEXT_ERROR")}
        >
          {t("log.deleteMeeting")}
        </Typography>
        {meetingName && (
          <Tooltip message={meetingName} classes="mg mt-4">
            <Typography type="subtitle2" ellipsis>
              {meetingName}
            </Typography>
          </Tooltip>
        )}
      </Box>
      <Typography type="subtitle2" classes="mg mt-24">
        {t("meeting.meetingId")}
      </Typography>
      <Typography type="body2_rg" classes="mg mt-8">
        {asideId?.split("@@")[1]}
      </Typography>
    </Box>
  ) : (
    <Box classes={classNames("meetingAside", classes)}>
      <Box height={160} alignItems="center" justifyContent="center">
        <img
          src={require("../../shared/images/pages/Meeting/noMeeting.png")}
          alt={"thumbnail"}
          width={157}
          height={84}
        />
      </Box>
      <Typography
        type="body2_rg"
        exactColor={getColor("COLOR_TEXT_SECONDARY")}
        textAlign="center"
      >
        {t("meeting.noSelect")}
      </Typography>
    </Box>
  );
};
export default MeetingAside;
